import {
  Button,
  Modal,
  Popconfirm,
  Table,
  Tag,
  Select,
  Tooltip,
  notification,
  Input,
} from 'antd'
import React, { useEffect, useMemo, useState, useContext, useRef } from 'react'
import { formatDeliveryDays } from 'src/utils/deliveryDays'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Title from 'src/branding/Title'
import FadeIn from 'src/components/styled/FadeIn'
import { stickyActionsColumn } from 'src/components/styled/horizontalScroll'
import {
  addSupplier,
  deleteSupplier,
  // downloadSupplierVenuesCSV,
  editSupplier,
  fetchAllSuppliers,
  rebuildSupplierSearchIndex,
  resetSearchReindex,
  triggerIronWebPriceFileMappingExport,
} from 'src/features/SupplierSlice'
import EditSupplierModal from 'src/screens/suppliers/EditSupplierModal'
import SupplierCard from 'src/screens/suppliers/SupplierCard'
import styled, { ThemeContext } from 'styled-components/macro'
import Sentry from 'src/utils/sentry'
import { CSVButton } from '../products/Products'
import { API } from 'src/utils/interceptor'
import {
  RESPONSIVE_TABLE_SCROLL,
  filterResponsiveColumns,
} from 'src/utils/responsiveTable'
import { arrayToCSVSupplier } from '../products/Utils'
import { useWindowWidth } from 'src/utils/useWindowWidth'
import { showErrorToast } from 'src/utils/messageToast'
import { SuccessNotification } from 'src/notifications/SuccessNotification'
import { getSupplierCreateBrandContext } from 'src/utils/supplierCreateBrandTag'
import {
  findDuplicateSupplier,
  getDuplicateSupplierErrorMessage,
} from 'src/utils/supplierCreateForm'
import { showSupplierCreateSuccessNotification } from 'src/utils/supplierCreateNextSteps'
import { filterAdminSuppliersBySearch } from 'src/utils/adminSuppliersTable'
// import LoadingButton from 'src/components/styled/LoadingButton'

const { Search } = Input

export const calculateTotalSpend = array => {
  let totalPrice = 0
  array?.map(order => {
    totalPrice += order?.isSpecialOrder
      ? order?.OMSSpecialPrice
        ? order?.OMSSpecialPrice * order.quantity
        : order?.specialProductPrice * order.quantity
      : order?.OMSPrice * order.quantity
  })
  return totalPrice / 100
}

import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'

const Suppliers = () => {
  const blocked = useIsBlocked('suppliers')

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const dispatch = useDispatch()
  const history = useHistory()
  const [csvSuppliers, setCsvSuppliers] = useState([])
  const [csvLoading, setCsvLoading] = useState(false)
  const csvButton = useRef()
  const account = useSelector(state => state.auth.account)
  const isPlatformAdmin = account?.isAdmin
  const supplierAdmin = account && account.supplierAdmin
  const [selectedSupplier, setSelectedSupplier] = useState(
    supplierAdmin ? account.supplier._id : null,
  )
  const windowWidth = useWindowWidth()
  const theme = useContext(ThemeContext)
  const isMobile = windowWidth <= theme.breakpoints.lg
  const { Option } = Select
  const [editingSupplier, setEditingSupplier] = useState(null)
  const [isSavingSupplier, setIsSavingSupplier] = useState(false)
  const [isCreateSupplierModalVisible, setIsCreateSupplierModalVisible] =
    useState(false)
  const [isCreatingSupplier, setIsCreatingSupplier] = useState(false)
  const [isSendingIronWebMapping, setIsSendingIronWebMapping] = useState(false)
  const [tableSearchTerm, setTableSearchTerm] = useState('')
  const suppliers = useSelector(state => state.suppliers.suppliers)
  const createBrandContext = getSupplierCreateBrandContext(account)
  const filteredSuppliers = useMemo(
    () => filterAdminSuppliersBySearch(suppliers, tableSearchTerm),
    [suppliers, tableSearchTerm],
  )

  useEffect(() => {
    if (suppliers.length === 0) dispatch(fetchAllSuppliers())
  }, [dispatch])

  const stringSort = (a, b) => String(a).trim().localeCompare(String(b).trim())

  const handleCreateSupplier = async supplier => {
    const duplicate = findDuplicateSupplier(
      suppliers,
      supplier.title,
      supplier.secondTitle,
    )

    if (duplicate) {
      showErrorToast(
        getDuplicateSupplierErrorMessage(supplier.title, supplier.secondTitle),
      )
      return false
    }

    setIsCreatingSupplier(true)
    const result = await dispatch(addSupplier(supplier))
    setIsCreatingSupplier(false)

    if (result?.success === false) {
      showErrorToast(result.error || 'Could not create supplier')
      return false
    }

    if (result?.success) {
      setIsCreateSupplierModalVisible(false)
      showSupplierCreateSuccessNotification(result.supplier, history)
      return true
    }

    showErrorToast('Could not create supplier')
    return false
  }

  // const onDownLoadCSV = supplierId => {
  //   dispatch(downloadSupplierVenuesCSV(supplierId))
  // }

  return (
    <Wrapper>
      <Title>Suppliers</Title>
      <DivContainer gutter={16}>
        <DivFlex>
          <Select
            placeholder='Select a supplier'
            style={{ width: isMobile ? '100%' : 250 }}
            showSearch
            optionFilterProp='children'
            notFoundContent='No suppliers found matching your search'
            onChange={e => {
              setSelectedSupplier(e)
            }}
          >
            {suppliers.map(supplier => {
              return (
                <Option key={supplier._id} value={supplier._id}>
                  {supplier.title}&nbsp;{supplier.secondTitle}
                </Option>
              )
            })}
          </Select>
          <Search
            placeholder='Search by title, email, account number…'
            value={tableSearchTerm}
            onChange={e => setTableSearchTerm(e.target.value)}
            onSearch={value => setTableSearchTerm(value)}
            style={{ width: isMobile ? '100%' : 300 }}
            allowClear
          />
          <CSVButton
            ref={csvButton}
            isMobile={isMobile}
            data={csvSuppliers}
            fetchCSVData={async () => {
              if (!selectedSupplier) {
                alert('Choose a supplier first')
                return false
              }
              setCsvLoading(true)
              try {
                const { data } = await API.get(
                  'api',
                  `venueSuppliers/${selectedSupplier}`,
                )
                if (data) {
                  const formattedSuppliers = arrayToCSVSupplier(data)
                  setCsvSuppliers(formattedSuppliers)
                  await csvButton.current.link.click()
                  setCsvSuppliers([])
                  setCsvLoading(false)
                }
              } catch (e) {
                console.log(e)
                Sentry.captureException(e)
                setCsvSuppliers([])
                setCsvLoading(false)
              }
            }}
            filename={`oms-suppliers-export.csv`}
            loading={csvLoading}
          >
            Download CSV
          </CSVButton>
          <Button
            type='primary'
            onClick={() => setIsCreateSupplierModalVisible(true)}
          >
            Add supplier
          </Button>
          {account?.isAdmin === true && (
            <Tooltip title='Manual send in addition to daily 3am UK send'>
              <Button
                type='primary'
                loading={isSendingIronWebMapping}
                onClick={async () => {
                  setIsSendingIronWebMapping(true)
                  const result = await dispatch(
                    triggerIronWebPriceFileMappingExport(),
                  )
                  setIsSendingIronWebMapping(false)

                  if (!result || result.success === false) {
                    notification.error({
                      message: 'IronWeb export failed',
                      description:
                        result?.error ||
                        result?.message ||
                        'Unable to send the price file mapping to IronWeb.',
                    })
                    return
                  }

                  const summary = result?.manualSummary || {}
                  SuccessNotification({
                    message: 'IronWeb export sent',
                    description: `Exported ${
                      summary.rowsExported ?? result.exportedRows ?? 0
                    } rows and skipped ${
                      summary.rowsSkipped ?? result.skippedRows ?? 0
                    }.`,
                  })
                }}
              >
                Send Price File Mapping to IronWeb
              </Button>
            </Tooltip>
          )}
        </DivFlex>
      </DivContainer>
      <Table
        scroll={RESPONSIVE_TABLE_SCROLL}
        dataSource={filteredSuppliers.map(supplier => ({
          ...supplier,
          key: supplier._id,
        }))}
        locale={{
          emptyText: tableSearchTerm.trim()
            ? 'No suppliers match your search'
            : 'No suppliers',
        }}
        loading={suppliers.length < 1}
        pagination={{
          position: 'both',
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        columns={filterResponsiveColumns(
          [
            {
              title: 'Title',
              dataIndex: 'title',
              key: 'title',
              className: 'restrictShrink',
              sorter: (a, b) => stringSort(a.title, b.title),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Second title',
              dataIndex: 'secondTitle',
              key: 'secondTitle',
              className: 'restrictShrink',
              hideBelow: 1366,
              sorter: (a, b) => stringSort(a.secondTitle, b.secondTitle),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Order email',
              dataIndex: 'emailAddress',
              key: 'emailAddress',
              className: 'restrictShrink',
              sorter: (a, b) => stringSort(a.emailAddress, b.emailAddress),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Number of Venues',
              dataIndex: 'venueCount',
              key: 'venueCount',
              className: 'restrictShrink',
              render: venueCount => (
                <span
                  style={{
                    fontWeight: 'bold',
                    color: venueCount > 0 ? '#52c41a' : '#999',
                    fontSize: '14px',
                  }}
                >
                  {venueCount || 0}
                </span>
              ),
              sorter: (a, b) => (a.venueCount || 0) - (b.venueCount || 0),
              sortDirections: ['descend', 'ascend'],
            },
            {
              title: 'Account number',
              dataIndex: 'accountNumber',
              key: 'accountNumber',
              className: 'restrictShrink',
              hideBelow: 1280,
              sorter: (a, b) => stringSort(a.accountNumber, b.accountNumber),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Minimum order',
              dataIndex: 'minOrderValue',
              key: 'minOrderValue',
              className: 'restrictShrink',
              hideBelow: 1093,
              render: text => <span>£{text ? text / 100 : 0}</span>,
              sorter: (a, b) => a.minOrderValue - b.minOrderValue,
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Delivery days',
              dataIndex: 'deliveryDays',
              key: 'deliveryDays',
              className: 'restrictShrink',
              hideBelow: 1093,
              render: text => {
                return <span>{formatDeliveryDays(text).join(', ')}</span>
              },
              filters: [
                {
                  text: 'Mon',
                  value: 'mon',
                },
                {
                  text: 'Tue',
                  value: 'tue',
                },
                {
                  text: 'Wed',
                  value: 'wed',
                },
                {
                  text: 'Thu',
                  value: 'thu',
                },
                {
                  text: 'Fri',
                  value: 'fri',
                },
                {
                  text: 'Sat',
                  value: 'sat',
                },
                {
                  text: 'Sun',
                  value: 'sun',
                },
              ],
              onFilter: (value, record) => record.deliveryDays.includes(value),
            },
            {
              title: 'Credit status',
              dataIndex: 'allowsCreditPurchase',
              key: 'allowsCreditPurchase',
              className: 'restrictShrink',
              render: (text, supplier) => {
                return (
                  <Tag
                    color={supplier.allowsCreditPurchase ? 'green' : 'orange'}
                  >
                    {supplier.allowsCreditPurchase
                      ? 'Allows credit'
                      : 'Pay on order'}
                  </Tag>
                )
              },
            },
            {
              title: 'Search index',
              key: 'searchIndex',
              className: 'restrictShrink',
              hideBelow: 1366,
              render: (text, supplier) =>
                supplier.searchReindexResponse ? (
                  supplier.searchReindexResponse.success ? (
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        dispatch(resetSearchReindex({ id: supplier._id }))
                      }
                    >
                      {supplier.searchReindexResponse.queued
                        ? 'Rebuild in progress'
                        : `Reindexed ${parseInt(
                            supplier.searchReindexResponse.recordsUpserted,
                          ).toLocaleString()} products`}
                    </span>
                  ) : (
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        dispatch(resetSearchReindex({ id: supplier._id }))
                      }
                    >
                      Failed to rebuild
                    </span>
                  )
                ) : (
                  <Button
                    type='secondary'
                    loading={supplier.loadingSearchReindex}
                    onClick={() => {
                      dispatch(rebuildSupplierSearchIndex(supplier))
                    }}
                  >
                    <span>Rebuild</span>
                  </Button>
                ),
            },
            {
              title: 'Action',
              key: 'action',
              className: 'restrictShrink sticky-actions-col',
              onCell: () => ({ className: 'sticky-actions-cell' }),
              render: (text, supplier) => (
                <>
                  <StyledButton
                    type={'dashed'}
                    onClick={() => {
                      setEditingSupplier(supplier)
                    }}
                  >
                    <span>Edit</span>
                  </StyledButton>
                  &nbsp;
                  <Popconfirm
                    title='Are you sure to delete this supplier?'
                    placement='topRight'
                    onConfirm={() => {
                      dispatch(deleteSupplier(supplier))
                    }}
                    okText='Yes'
                    cancelText='No'
                  >
                    <StyledButton type={'danger'}>
                      <span>Delete</span>
                    </StyledButton>
                  </Popconfirm>
                  {/* <LoadingButton
                  style={{
                    marginTop: '3px',
                  }}
                  onClick={() => {
                    onDownLoadCSV(supplier?._id)
                  }}
                >
                  Download Venues
                </LoadingButton> */}
                </>
              ),
            },
          ],
          windowWidth,
        )}
      />

      <AddSupplierHint>
        To add a new supplier, use the <strong>Add supplier</strong> button at
        the top of this page.
      </AddSupplierHint>

      <Modal
        title='Add supplier'
        visible={isCreateSupplierModalVisible}
        onCancel={() => {
          if (isCreatingSupplier) return
          setIsCreateSupplierModalVisible(false)
        }}
        footer={null}
        destroyOnClose
        width={720}
      >
        <SupplierCard
          inModal
          isSubmitting={isCreatingSupplier}
          isPlatformAdmin={isPlatformAdmin}
          createBrandContext={createBrandContext}
          saveSupplier={handleCreateSupplier}
        />
      </Modal>

      <Modal
        title='Edit supplier'
        visible={editingSupplier !== null}
        confirmLoading={isSavingSupplier}
        onOk={async () => {
          if (!editingSupplier) return

          setIsSavingSupplier(true)
          const result = await dispatch(
            editSupplier({
              ...editingSupplier,
            }),
          )
          setIsSavingSupplier(false)

          if (result === false) return

          // Show toast notification if auto-approve is enabled
          if (editingSupplier?.autoApproveOrders) {
            SuccessNotification({
              message: 'Auto-approve enabled',
              description:
                'All orders to this supplier will now be marked as approved automatically.',
            })
          }

          setEditingSupplier(null)
        }}
        onCancel={() => {
          if (isSavingSupplier) return
          setEditingSupplier(null)
        }}
      >
        {editingSupplier && (
          <EditSupplierModal
            supplier={editingSupplier}
            isPlatformAdmin={isPlatformAdmin}
            setEditingSupplier={supplier => {
              setEditingSupplier(supplier)
            }}
          />
        )}
      </Modal>
    </Wrapper>
  )
}

const DivContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
`

const Wrapper = styled(FadeIn)`
  ${stickyActionsColumn}
  .restrictShrink {
    min-width: 95px;
  }
`
const StyledButton = styled(Button)`
  margin: 3px 3px 3px 0;
  width: 80px;
`
const DivFlex = styled.div`
  display: flex;
  gap: 20px;
  width: 60%;
  justify-content: ${props => (props.flexEnd ? 'flex-end' : 'flex-start')};
  padding-top: 8px;
  flex-wrap: wrap;
`

const AddSupplierHint = styled.p`
  margin: 16px 0 0;
  color: ${props => props.theme.colours.mushroom || '#666'};
  font-size: 14px;
`

export default Suppliers
